import { type TFunction } from 'i18next'
import { produce } from 'immer'
import * as R from 'remeda'

import {
  calcHeatmapRatio,
  type CellInfo,
  compact,
  type DataDisplayType,
  exists,
  getSortIconBySortValue,
  mapNineSegment,
  nineSegmentAttributeI18nKeyMap,
  nineSegmentNumberWithIntentionI18nKeyMap,
  nineSegments,
  NumFormat,
  QuestionOp,
  reversedNineSegments,
  type RowInfo,
} from '~/utils'

import {
  type NineSegmentColumnKey,
  nineSegmentIndicatorHelpMap,
  nineSegmentIndicatorLabelI18nKeyMap,
  nineSegmentIndicators,
  type NineSegmentsData,
} from './nineSegments'
import { filterVisibleAnswersArrayData } from './visibleAnswersArrayData'

// Minimal interfaces using structural typing so both hooks and basic output can satisfy them
type AnswerData = NineSegmentsData & {
  answerOption: { option: number; text: string }
  question: { question_id: string; question_text: string }
}

type CustomerAttributeData = NineSegmentsData & {
  customerAttribute: { id: string; name: string }
  customerAttributeGroup: { name: string }
  number: number
}

type BuildNineSegmentsTableInfoParams = {
  addHighlightProps: (key: string) => Record<string, unknown>
  answersArrayData: AnswerData[][]
  customerAttributesArrayData: CustomerAttributeData[][]
  dataDisplayType: DataDisplayType
  hiddenColumnKeys: NineSegmentColumnKey[]
  hiddenRowIds: string[]
  sampleSize: number
  sortValue: { ascending: boolean; columnKey: string } | null
  t: TFunction
  tamRatio: number
  totalData: NineSegmentsData
}

export const buildNineSegmentsTableInfo = ({
  addHighlightProps,
  answersArrayData,
  customerAttributesArrayData,
  dataDisplayType,
  hiddenColumnKeys,
  hiddenRowIds,
  sampleSize,
  sortValue,
  t,
  tamRatio,
  totalData,
}: BuildNineSegmentsTableInfoParams): RowInfo<NineSegmentColumnKey>[] => {
  const visibleAnswersArrayData = filterVisibleAnswersArrayData(
    answersArrayData,
    hiddenRowIds,
  )
  const visibleCustomerAttributesArrayData = customerAttributesArrayData
    .map((customerAttributesData) =>
      customerAttributesData.filter(
        (customerAttributeData) =>
          !hiddenRowIds.includes(customerAttributeData.customerAttribute.id),
      ),
    )
    .filter((customerAttributesData) => 0 < customerAttributesData.length)

  const rowSpan =
    4 +
    visibleAnswersArrayData.length * 2 +
    visibleAnswersArrayData.flat().length +
    visibleCustomerAttributesArrayData.length * 2 +
    visibleCustomerAttributesArrayData.flat().length

  const visibleNineSegments = R.difference(
    reversedNineSegments,
    hiddenColumnKeys,
  )

  const has8 = visibleNineSegments.includes('nineSegment8')
  const has7 = visibleNineSegments.includes('nineSegment7')
  const has6 = visibleNineSegments.includes('nineSegment6')
  const has5 = visibleNineSegments.includes('nineSegment5')
  const has4 = visibleNineSegments.includes('nineSegment4')
  const has3 = visibleNineSegments.includes('nineSegment3')
  const has2 = visibleNineSegments.includes('nineSegment2')
  const has1 = visibleNineSegments.includes('nineSegment1')
  const title1NineSegments = compact([
    has8 ? 'nineSegment8' : has7 ? 'nineSegment7' : false,
    has6 ? 'nineSegment6' : has5 ? 'nineSegment5' : false,
    has4 ? 'nineSegment4' : has3 ? 'nineSegment3' : false,
    has2 ? 'nineSegment2' : has1 ? 'nineSegment1' : false,
  ])
  const title1NineSegmentColSpans = compact([
    has8 ? (has7 ? 2 : 1) : has7 ? 1 : false,
    has6 ? (has5 ? 2 : 1) : has5 ? 1 : false,
    has4 ? (has3 ? 2 : 1) : has3 ? 1 : false,
    has2 ? (has1 ? 2 : 1) : has1 ? 1 : false,
  ])

  // "全体" (Total) and the labels for each of the nine segments.
  const title1RowInfo: RowInfo<NineSegmentColumnKey> = {
    questionText: { isColumnHeader: true, colSpan: 3, rowSpan: 2 },
    total: {
      align: 'center',
      icon: getSortIconBySortValue(sortValue, 'total'),
      isColumnHeader: true,
      isHeader: true,
      rowSpan: 2,
      text: t('overall'),
    },
    nineSegment9: {
      align: 'center',
      isColumnHeader: true,
      isHeader: true,
      text: t(nineSegmentAttributeI18nKeyMap.nineSegment9),
    },
    ...R.mapToObj(title1NineSegments, (nineSegment, index) => [
      nineSegment,
      {
        align: 'center',
        isColumnHeader: true,
        isHeader: true,
        colSpan: title1NineSegmentColSpans[index],
        text: t(nineSegmentAttributeI18nKeyMap[nineSegment]),
      } satisfies CellInfo,
    ]),
    ...R.mapToObj(nineSegmentIndicators, (indicator) => [
      indicator,
      {
        align: 'center',
        help: nineSegmentIndicatorHelpMap[indicator],
        icon: getSortIconBySortValue(sortValue, indicator),
        isColumnHeader: true,
        isHeader: true,
        rowSpan: 2,
        text: t(nineSegmentIndicatorLabelI18nKeyMap[indicator]),
      } satisfies CellInfo,
    ]),
  }

  // 未認知、消極、積極など
  const title2RowInfo: RowInfo<NineSegmentColumnKey> = {
    ...mapNineSegment((nineSegment) => ({
      align: 'center',
      icon: getSortIconBySortValue(sortValue, nineSegment),
      isColumnHeader: true,
      isHeader: true,
      text: t(nineSegmentNumberWithIntentionI18nKeyMap[nineSegment]),
    })),
  }

  const indicatorMinMaxMap = R.mapToObj(nineSegmentIndicators, (indicator) => {
    const ratioArray = R.concat(
      answersArrayData.flatMap((answersData) =>
        answersData.map((answerData) => answerData.ratios[indicator]),
      ),
      visibleCustomerAttributesArrayData.flatMap((customerAttributesData) =>
        customerAttributesData.map(
          (customerAttributeData) => customerAttributeData.ratios[indicator],
        ),
      ),
    )

    return [
      indicator,
      { max: Math.max(...ratioArray), min: Math.min(...ratioArray) },
    ]
  })

  const getCountRowInfo = <T extends NineSegmentsData>(
    dataArray: T[],
    data: T,
    index: number,
    getQuestionText: (data: T) => number | string,
    getAnswerNumber: (data: T, index: number) => number | string,
    getAnswerText: (data: T) => number | string,
  ): RowInfo<NineSegmentColumnKey> => {
    const questionText =
      index === 0
        ? {
            isHeader: true,
            text: getQuestionText(data),
            rowSpan: dataArray.length,
          }
        : undefined

    const deriveCellIdentity = (indicator: string) =>
      `9seg-${getQuestionText(data)}--${getAnswerText(data)}--${indicator}`

    return {
      questionText,
      answerNumber: {
        isHeader: true,
        text: getAnswerNumber(data, index),
      },
      answerText: {
        isHeader: true,
        text: getAnswerText(data),
        textCharacterLimit: 30,
      },
      ...R.mapToObj(['total', ...nineSegments] as const, (indicator) => {
        const ratio = data.ratios[indicator]
        const count = data.counts[indicator]
        const key = deriveCellIdentity(indicator)

        return [
          indicator,
          {
            ratio,
            text: NumFormat.forCount(count, ratio, tamRatio, dataDisplayType),
            ...addHighlightProps(key),
          } satisfies CellInfo,
        ]
      }),
      ...R.mapToObj(
        nineSegmentIndicators.filter(
          (indicator) => !indicator.endsWith('Diff'),
        ),
        (indicator) => {
          const ratio = data.ratios[indicator]
          const count = data.counts[indicator]
          const key = deriveCellIdentity(indicator)

          return [
            indicator,
            {
              ratio,
              text: NumFormat.forCount(count, ratio, tamRatio, dataDisplayType),
              ...addHighlightProps(key),
            } satisfies CellInfo,
          ]
        },
      ),
      ...R.mapToObj(
        nineSegmentIndicators.filter((indicator) => indicator.endsWith('Diff')),
        (indicator) => {
          const ratio = data.ratios[indicator]
          const count = data.counts[indicator]
          const key = deriveCellIdentity(indicator)
          const [text, copyValue] = NumFormat.countToPts(
            count,
            ratio,
            tamRatio,
            dataDisplayType,
          )
          const { max, min } = indicatorMinMaxMap[indicator]

          return [
            indicator,
            {
              copyValue,
              heatmap: true,
              ratio: calcHeatmapRatio(ratio, max, min),
              text,
              ...addHighlightProps(key),
            } satisfies CellInfo,
          ]
        },
      ),
    }
  }

  return [
    // ヘッダー1
    produce(title1RowInfo, (draft) => {
      for (const indicator of nineSegmentIndicators) {
        const cellInfo = draft[indicator]
        if (exists(cellInfo)) {
          // TODO: ? が表示されるかどうかでセルの高さを変えているが、UIの仕様が変わったらバグるのでより良い方法を検討したい
          cellInfo.rowSpan = exists(cellInfo.help) ? 2 : 4
        }
      }
      draft.totalPad = { rowSpan }
      draft.currentUserPad = { rowSpan }
      draft.recognizedPad = { rowSpan }
      draft.estrangedPad = { rowSpan }
      draft.generalPad = { rowSpan }
      draft.loyalPad = { rowSpan }
      draft.recognizedPositiveGeneralPositivePad = { rowSpan }
      draft.recognizedNegativeGeneralPositivePad = { rowSpan }
      draft.estrangedPositiveGeneralPositivePad = { rowSpan }
      draft.estrangedNegativeGeneralPositivePad = { rowSpan }
      draft.generalPositiveLoyalPositivePad = { rowSpan }
      draft.generalNegativeLoyalPositivePad = { rowSpan }
      draft.recognizedNegativeLoyalPositivePad = { rowSpan }
      draft.estrangedNegativeLoyalPositivePad = { rowSpan }
    }),
    // ヘッダー2
    title2RowInfo,
    // 分布
    {
      questionText: {
        colSpan: 3,
        isHeader: true,
        text: t('distribution'),
      },
      total: {
        text: NumFormat.forRatio(totalData.counts.total / sampleSize),
        ...addHighlightProps('9seg-total--distribution'),
      },
      ...mapNineSegment((nineSegment) => ({
        text: NumFormat.forRatio(totalData.ratios[nineSegment]),
        ...addHighlightProps(`9seg-${nineSegment}--distribution`),
      })),
      ...R.mapToObj(
        nineSegmentIndicators.filter(
          (indicator) => !indicator.endsWith('Diff'),
        ),
        (indicator) => [
          indicator,
          {
            text: NumFormat.forRatio(totalData.ratios[indicator]),
            ...addHighlightProps(`9seg-${indicator}--distribution`),
          } satisfies CellInfo,
        ],
      ),
    },
    // サンプル数
    {
      questionText: {
        colSpan: 3,
        isHeader: true,
        text: t('sample_size'),
      },
      total: {
        text: NumFormat.format(totalData.counts.total),
        ...addHighlightProps('9seg-total--sampleSize'),
      },
      ...mapNineSegment((nineSegment) => ({
        text: NumFormat.format(totalData.counts[nineSegment]),
        ...addHighlightProps(`9seg-${nineSegment}--sampleSize`),
      })),
      ...R.mapToObj(
        nineSegmentIndicators.filter(
          (indicator) => !indicator.endsWith('Diff'),
        ),
        (indicator) => [
          indicator,
          {
            text: NumFormat.format(totalData.counts[indicator]),
            ...addHighlightProps(`9seg-${indicator}--sampleSize`),
          } satisfies CellInfo,
        ],
      ),
    },
    // 設問
    ...visibleAnswersArrayData.flatMap((answersData) => [
      // 選択肢
      ...answersData.map((answerData, answerDataIndex) =>
        getCountRowInfo(
          answersData,
          answerData,
          answerDataIndex,
          ({ question }) => QuestionOp.toText(question),
          ({ answerOption }) => answerOption.option,
          ({ answerOption }) => answerOption.text,
        ),
      ),
      // 設問間のタイトル
      title1RowInfo,
      title2RowInfo,
    ]),
    // 顧客属性グループ
    ...visibleCustomerAttributesArrayData.flatMap((customerAttributesData) => [
      // 顧客属性
      ...customerAttributesData.map(
        (customerAttributeData, customerAttributeDataIndex) =>
          getCountRowInfo(
            customerAttributesData,
            customerAttributeData,
            customerAttributeDataIndex,
            ({ customerAttributeGroup }) => customerAttributeGroup.name,
            ({ number }) => number,
            ({ customerAttribute }) => customerAttribute.name,
          ),
      ),
      // 顧客属性グループ間のタイトル
      title1RowInfo,
      title2RowInfo,
    ]),
  ]
}
